import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import OrangeScoreBadge from "@/hq/OrangeScoreBadge";
import { Send, CheckCircle2, Loader2, Mail } from "lucide-react";

/**
 * ClientPitchTab — Phase 3G.9 Sales Pitch Centre.
 *
 * Answers "What should I pitch this client, why, has it been sent, what
 * happened?" for the ONLY two pitch types the Phase 3G Sales Pitch Engine
 * supports: WEBSITE + ORANGE OPTIMISE.
 *
 * Data source is the canonical `/pitch/{type}/context` endpoint which:
 *   - returns `last_pitch` (with sent_at, subject, delivery_status) if any
 *   - reuses the same `_load_latest_scan` used everywhere else, so the
 *     Optimise pitch sees the exact immutable scan that was linked when
 *     the client was created from a Single Scan
 *   - enforces duplicate-window protection server-side
 *
 * We do NOT invent recommendation reasons — the reason strings come from
 * the client's real service interest + real scan score.
 */
export default function ClientPitchTab({ businessId, serviceSlots, orangeScore, onPitchClick }) {
  const [ctx, setCtx] = useState({ website: null, optimise: null, crm: null, full: null });
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      hqApi.get(`/control/clients/${businessId}/pitch/website/context`),
      hqApi.get(`/control/clients/${businessId}/pitch/optimise/context`),
      hqApi.get(`/control/clients/${businessId}/pitch/crm/context`),
      hqApi.get(`/control/clients/${businessId}/pitch/full/context`),
    ]).then(([w, o, cr, fu]) => {
      setCtx({
        website:  w.status === "fulfilled" ? { ok: true, data: w.value.data } : { ok: false, err: w.reason?.response?.data?.detail },
        optimise: o.status === "fulfilled" ? { ok: true, data: o.value.data } : { ok: false, err: o.reason?.response?.data?.detail },
        crm:      cr.status === "fulfilled" ? { ok: true, data: cr.value.data } : { ok: false, err: cr.reason?.response?.data?.detail },
        full:     fu.status === "fulfilled" ? { ok: true, data: fu.value.data } : { ok: false, err: fu.reason?.response?.data?.detail },
      });
    }).finally(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { load(); }, [load]);
  // Refresh when the tab regains focus — picks up a just-sent pitch.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const interestedIn = useMemo(() => {
    const s = serviceSlots || [];
    return {
      website:  s.find((x) => x.service_key === "premium_website"),
      optimise: s.find((x) => x.service_key === "orange_optimise"),
      crm:      s.find((x) => x.service_key === "crm_business_system"),
    };
  }, [serviceSlots]);

  if (loading) {
    return <Card className="p-6 flex items-center justify-center text-[#8A8A8A]" data-testid="hq-client-pitch-loading">
      <Loader2 className="animate-spin mr-2" size={16}/> Loading pitch context…
    </Card>;
  }

  return (
    <div className="space-y-4" data-testid="hq-client-pitch-tab">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Pitch</div>
        <p className="mt-1 text-sm text-[#4A4A4A]">Recommended sales opportunities for this client. Based on their real service interest and canonical Orange Optimise scan data.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <PitchCard
          testId="hq-pitch-website"
          title="Website"
          interested={!!interestedIn.website}
          interestStatus={interestedIn.website?.status}
          ctx={ctx.website}
          reasonBuilder={buildWebsiteReason}
          onPreview={() => onPitchClick?.("website")}
        />
        <PitchCard
          testId="hq-pitch-optimise"
          title="Orange Optimise"
          interested={!!interestedIn.optimise}
          interestStatus={interestedIn.optimise?.status}
          orangeScore={orangeScore}
          ctx={ctx.optimise}
          reasonBuilder={buildOptimiseReason}
          onPreview={() => onPitchClick?.("optimise")}
        />
        <PitchCard
          testId="hq-pitch-crm"
          title="CRM & Business Systems"
          interested={!!interestedIn.crm}
          interestStatus={interestedIn.crm?.status}
          ctx={ctx.crm}
          reasonBuilder={buildCrmReason}
          onPreview={() => onPitchClick?.("crm")}
        />
        <PitchCard
          testId="hq-pitch-full"
          title="Full OrangeBySite Pitch"
          ctx={ctx.full}
          reasonBuilder={buildFullReason}
          onPreview={() => onPitchClick?.("full")}
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- card
function PitchCard({ testId, title, interested, interestStatus, orangeScore, ctx, reasonBuilder, onPreview }) {
  const last = ctx?.ok ? ctx.data?.last_pitch : null;
  const stats = ctx?.ok ? (ctx.data?.stats || {}) : {};
  const model = ctx?.ok ? ctx.data?.model : null;
  const available = !!ctx?.ok;
  const status = deriveStatus({ available, last, interestStatus });
  const reason = reasonBuilder({ available, ctx, last, interested, interestStatus, orangeScore });
  const openRate = stats.sent > 0 ? Math.round((stats.opened / stats.sent) * 100) : null;

  return (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Pitch product</div>
          <h3 className="mt-1 font-black text-lg" data-testid={`${testId}-title`}>{title}</h3>
        </div>
        {title === "Orange Optimise" && orangeScore != null ? (
          <OrangeScoreBadge score={orangeScore} size="sm" testId={`${testId}-score-badge`}/>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`${testId}-status-row`}>
        <StatusPill status={status} testId={`${testId}-status`}/>
        {interested && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.06em] uppercase text-[#8A6D00] bg-[#FFF9E6] border border-[#F5E4B0]"
                data-testid={`${testId}-interested-chip`}>
            Interested in {title}
          </span>
        )}
        {stats.sent > 0 && (
          <span
            data-testid={`${testId}-engagement-pill`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-[0.06em] uppercase text-[#2E7CF6] bg-[#F0F7FF] border border-[#D6E7FF]"
            title={`Sent ${stats.sent} · delivered ${stats.delivered} · opened ${stats.opened} · clicked ${stats.clicked}`}
          >
            {stats.sent} sent → {stats.opened} opened{openRate !== null ? ` · ${openRate}%` : ""}
          </span>
        )}
      </div>

      <p className="mt-3 text-sm text-[#4A4A4A]" data-testid={`${testId}-reason`}>{reason}</p>

      {last && (
        <div className="mt-3 p-3 rounded-lg bg-[#F6F5F2] text-[12px]" data-testid={`${testId}-sent-details`}>
          <div className="text-[10px] tracking-[0.20em] uppercase font-black text-[#8A8A8A]">Last sent</div>
          <div className="mt-1 font-black">{new Date(last.sent_at).toLocaleString()}</div>
          {last.subject ? <div className="mt-0.5 text-[#4A4A4A]">Subject: {last.subject}</div> : null}
          {last.delivery_status ? (
            <div className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
              Delivery: {last.delivery_status}
            </div>
          ) : null}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid={`${testId}-preview`}
          onClick={onPreview}
          disabled={!available}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${
            available ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"
          }`}
        >
          {last ? <Mail size={13}/> : <Send size={13}/>}
          {last ? `Preview ${title} follow-up` : `Preview ${title} pitch`}
        </button>
      </div>

      {!available && (
        <div className="mt-3 p-2 rounded-lg bg-[#FFF7EE] border border-[#FCE1C7] text-[11px] text-[#8A4A00]"
             data-testid={`${testId}-unavailable`}>
          {ctx?.err?.detail || ctx?.err || "Pitch context is not available yet — a completed website scan is required for Orange Optimise."}
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------- helpers
const STATUS_TONE = {
  RECOMMENDED:     { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  SENT:            { fg: "#2E7CF6", bg: "#F0F7FF", bd: "#D6E7FF" },
  CONVERTED:       { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  "NOT RECOMMENDED": { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
  UNAVAILABLE:     { fg: "#8A4A00", bg: "#FFF7EE", bd: "#FCE1C7" },
};
function StatusPill({ status, testId }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.RECOMMENDED;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.14em] uppercase"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
    >
      {status === "SENT" || status === "CONVERTED" ? <CheckCircle2 size={11}/> : null}
      {status}
    </span>
  );
}

function deriveStatus({ available, last, interestStatus }) {
  if ((interestStatus || "").toLowerCase() === "active") return "CONVERTED";
  if (!available) return "UNAVAILABLE";
  if (last) return "SENT";
  return "RECOMMENDED";
}

function buildWebsiteReason({ available, last, interested }) {
  if (!available) return "This pitch cannot be rendered yet — check that the client has a valid contact and website.";
  if (last)       return `The Website pitch was sent on ${new Date(last.sent_at).toLocaleDateString()}. Use the preview button above to send a follow-up if needed.`;
  if (interested) return "Client has expressed interest in a Website.";
  return "Website pitch is available. Recommend based on operator discretion.";
}

function buildOptimiseReason({ available, ctx, last, interested, orangeScore }) {
  if (!available) return "A completed Orange Optimise scan is required before this pitch can be sent.";
  if (last)       return `The Orange Optimise pitch was sent on ${new Date(last.sent_at).toLocaleDateString()}. The engine will refuse a duplicate send inside the protection window.`;
  if (orangeScore != null && orangeScore < 70) return `Orange Score ${orangeScore} — website health improvements identified. Optimise pitch is recommended.`;
  if (interested) return "Client has expressed interest in Orange Optimise.";
  if (orangeScore != null) return `Orange Score ${orangeScore}. Pitch available on operator discretion.`;
  return "Orange Optimise pitch is available.";
}

function buildCrmReason({ available, last, interested }) {
  if (!available) return "CRM pitch is not available yet — check that the client has a valid contact and email.";
  if (last)       return `The CRM pitch was sent on ${new Date(last.sent_at).toLocaleDateString()}. Use the preview button above to send a follow-up if needed.`;
  if (interested) return "Client has expressed interest in a bespoke CRM & business system.";
  return "Bespoke systems designed around the way this business actually works.";
}

function buildFullReason({ available, last }) {
  if (!available) return "Full OrangeBySite pitch is not available yet — check that the client has a valid contact and email.";
  if (last)       return `The Full OrangeBySite pitch was sent on ${new Date(last.sent_at).toLocaleDateString()}. Use the preview button above to send a follow-up if needed.`;
  return "Introduce the complete OrangeBySite ecosystem — bespoke CRM & Business Systems, Websites and Orange Optimise.";
}
